
import React, { useEffect, useRef } from 'react';
import { Search, ChevronRight, ChevronLeft, X } from 'lucide-react';

interface CanvasSearchProps {
  query: string;
  onQueryChange: (query: string) => void;
  matchCount: number;
  activeIndex: number;
  onNext: () => void;
  onClear: () => void;
}

export const CanvasSearch: React.FC<CanvasSearchProps> = ({
  query,
  onQueryChange,
  matchCount,
  activeIndex,
  onNext,
  onClear
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault();
        inputRef.current?.focus();
      }
      if (e.key === 'Escape' && document.activeElement === inputRef.current) {
        inputRef.current?.blur();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && matchCount > 0) {
      onNext();
    }
  };

  return (
    <div className="absolute top-4 left-4 z-10 w-full px-4 md:px-0 md:w-80 animate-in fade-in slide-in-from-top-2 duration-300">
      <div className="relative flex items-center bg-white/95 dark:bg-slate-900/95 backdrop-blur shadow-lg border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden transition-all focus-within:ring-2 focus-within:ring-teal-500/30 focus-within:border-teal-500">
        <div className="pl-3 text-slate-400">
          <Search size={16} />
        </div>
        
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Search nodes... (Ctrl+F)"
          className="flex-1 px-3 py-2.5 text-sm bg-transparent outline-none text-slate-800 dark:text-slate-100 placeholder-slate-400"
        />

        {query && (
          <div className="flex items-center pr-1 gap-1">
            {matchCount > 0 ? (
              <button
                onClick={onNext}
                className="flex items-center gap-1.5 px-2 py-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors group"
                title="Next match"
              >
                <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 font-mono">
                  {activeIndex + 1}/{matchCount}
                </span>
                <ChevronRight size={14} className="text-slate-400 group-hover:text-teal-600" />
              </button>
            ) : (
              <span className="text-[10px] font-bold text-red-500 px-2 uppercase tracking-tighter">No results</span>
            )}
            
            <div className="w-px h-4 bg-slate-200 dark:bg-slate-800 mx-1" />
            
            <button
              onClick={onClear}
              className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 rounded-md transition-colors"
              title="Clear search"
            >
              <X size={14} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
